import { produce } from 'immer'

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { CompanyData, CreateOrderItem, ProductComponent } from '@/lib/api.types'
import type { PrinterState } from '@/lib/printerState'
import { TicketPrice } from '@/types/tickets'

export type CartItemId = string | number

export interface CartItem {
  id: CartItemId
  productId: string
  productName: string
  coverImageUrl?: string
  name: string
  price: TicketPrice
  qty: number
  consumerCategoryId: string | null
  date: string
  startTime: string | null
  pickupMethod: 'onSite' | 'online'
  paymentMethod: string
  orderItemComponents: ProductComponent[]
  availabilityType?: 'TIME_SLOT' | 'WHOLE_DAY' | null
  unlockId?: number
  restricted?: boolean
}

export type StepName = 'CART' | 'PAYMENT_METHOD' | 'PAYMENT_PENDING' | 'CONFIRMATION' | 'ERROR'

interface CheckoutState {
  currentStep: StepName
  paymentError: string | null
  cart: CartItem[]
  paymentMethod?: 'creditCard' | 'blik'
  companyData?: CompanyData | null
  orderId?: string | number
  printerStatus?: PrinterState
  unlockedTickets: number[]
  checkoutStartedAt: number | null
}

const padDatePart = (value: number) => String(value).padStart(2, '0')
const getTodayDate = () => {
  const today = new Date()
  return `${today.getFullYear()}-${padDatePart(today.getMonth() + 1)}-${padDatePart(today.getDate())}`
}

const initialState: CheckoutState = {
  currentStep: 'CART',
  paymentError: null,
  paymentMethod: 'creditCard',
  companyData: null,
  cart: [],
  orderId: undefined,
  printerStatus: undefined,
  unlockedTickets: [],
  checkoutStartedAt: null
}

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    addToCart: (state, { payload }: PayloadAction<CartItem>) => {
      const item = state.cart.find(o => o.id === payload.id)
      const normalizedPayload = {
        ...payload,
        qty: Math.max(1, Number(payload.qty) || 1),
        date: payload.date || getTodayDate()
      }

      return item
        ? {
            ...state,
            cart: state.cart.map(cartItem => (cartItem.id === item.id ? { ...cartItem, ...normalizedPayload } : cartItem))
          }
        : {
            ...state,
            cart: [...state.cart, normalizedPayload]
          }
    },
    removeFromCart: (state, { payload }: PayloadAction<{ id: CartItemId; all?: boolean }>) => {
      const itemToRemove = state.cart.find(o => o.id.toString() === payload.id.toString())

      if (itemToRemove && itemToRemove.qty > 1 && !payload.all) {
        return {
          ...state,
          cart: produce(state.cart, draftState => {
            const foundItem = draftState.find(o => o.id === itemToRemove.id)
            if (foundItem) {
              foundItem.qty--
            }
          })
        }
      } else {
        const notRestrictedQty = state.cart.filter(item => !item.restricted).reduce((acc, item) => acc + item.qty, 0)
        if (notRestrictedQty === 1) {
          return {
            ...state,
            cart: state.cart.filter(
              o =>
                o.id.toString() !== payload.id.toString() &&
                (!o.restricted || (typeof o.unlockId === 'number' && state.unlockedTickets.includes(o.unlockId)))
            )
          }
        }
        return {
          ...state,
          cart: state.cart.filter(o => o.id.toString() !== payload.id.toString())
        }
      }
    },
    setQty: (state, { payload }: PayloadAction<{ id: CartItemId; qty: number }>) => {
      if (payload.qty <= 0) {
        return {
          ...state,
          cart: state.cart.filter(o => o.id !== payload.id)
        }
      }

      return {
        ...state,
        cart: produce(state.cart, draftState => {
          const foundItem = draftState.find(o => o.id === payload.id)
          if (foundItem) {
            foundItem.qty = payload.qty
          }
        })
      }
    },
    setCurrentStep: (state, { payload }: PayloadAction<StepName>) => {
      return {
        ...state,
        currentStep: payload
      }
    },
    setPaymentMethod: (state, { payload }) => {
      return {
        ...state,
        paymentMethod: payload
      }
    },
    setPaymentError: (state, { payload }) => {
      return {
        ...state,
        paymentError: payload
      }
    },
    setCompanyData: (state, { payload }: PayloadAction<CompanyData | null>) => {
      return {
        ...state,
        companyData: payload
      }
    },
    setOrderId: (state, { payload }: PayloadAction<number | string>) => {
      return {
        ...state,
        orderId: payload
      }
    },
    setPrinterStatus: (state, { payload }: PayloadAction<PrinterState>) => {
      return {
        ...state,
        printerStatus: payload
      }
    },
    setUnlockedTickets: (state, { payload }: PayloadAction<number[]>) => {
      return {
        ...state,
        unlockedTickets: payload
      }
    },
    addUnlockedTicket: (state, { payload }: PayloadAction<number>) => {
      return {
        ...state,
        unlockedTickets: [...state.unlockedTickets, payload]
      }
    },
    setCheckoutStartedAt: (state, { payload }: PayloadAction<number | null>) => {
      return {
        ...state,
        checkoutStartedAt: payload
      }
    },
    reset: () => {
      return {
        ...initialState
      }
    }
  }
})

const cartItems = (state: CheckoutState) => state.cart

export const {
  setCurrentStep,
  addToCart,
  removeFromCart,
  setPaymentMethod,
  setPaymentError,
  reset,
  setQty,
  setCompanyData,
  setOrderId,
  setPrinterStatus,
  setUnlockedTickets,
  addUnlockedTicket,
  setCheckoutStartedAt
} = checkoutSlice.actions

export const cartTotalSelector = createSelector(cartItems, cart =>
  cart.length > 0 ? cart.map(o => o.qty * o.price.grossAmount).reduce((acc, current) => acc + current) : 0
)

export const cartNotRestrictedQtySelector = createSelector(cartItems, cart =>
  cart.reduce((acc, item) => (item.restricted ? acc : acc + item.qty), 0)
)

export const cartRestrictedQtySelector = createSelector(cartItems, cart =>
  cart.reduce((acc, item) => (item.restricted ? acc + item.qty : acc), 0)
)

export const orderItemsSelector = createSelector(cartItems, cart =>
  cart.map(
    (item): CreateOrderItem => ({
      productId: item.productId,
      consumerCategoryId: item.consumerCategoryId,
      quantity: item.qty,
      date: item.date || getTodayDate(),
      startTime: item.startTime ?? null,
      pickupMethod: item.pickupMethod,
      paymentMethod: item.paymentMethod,
      orderItemComponents: item.orderItemComponents ?? []
    })
  )
)

export default checkoutSlice.reducer
